/**
 * Тонкая обёртка над официальным telegram-web-app.js.
 *
 * Сторонний SDK сюда не тянем: нужен ровно initData и пара вызовов темы,
 * а Telegram всё равно требует подключать свой скрипт с telegram.org.
 * Меньше зависимостей — меньше поводов для несовместимости при обновлениях.
 */

export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: Record<string, unknown>;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  version: string;
  ready(): void;
  expand(): void;
  close(): void;
  onEvent(event: string, handler: () => void): void;
  offEvent(event: string, handler: () => void): void;

  /** Bot API 8.0+. В старых клиентах метода нет. */
  requestFullscreen?(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  setBottomBarColor?(color: string): void;
  exitFullscreen?(): void;
  isFullscreen?: boolean;
  disableVerticalSwipes?(): void;
  /** Отступы под «чёлку» и системные элементы в полноэкранном режиме. */
  safeAreaInset?: { top: number; bottom: number; left: number; right: number };
  contentSafeAreaInset?: { top: number; bottom: number; left: number; right: number };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/**
 * initData — единственное, что доказывает бэкенду, кто перед ним.
 * Пустая строка означает, что страницу открыли не из Telegram.
 */
export function getInitData(): string {
  return getWebApp()?.initData ?? '';
}

export function isInsideTelegram(): boolean {
  return getInitData().length > 0;
}

/** Встроенный браузер Telegram, а не мини-приложение.
 *
 *  Отличается по составу параметров запуска. Открывая обычную ссылку,
 *  Telegram всё равно подкладывает `window.Telegram.WebApp` и передаёт
 *  тему, версию и платформу — но **не** `tgWebAppData`. Поэтому
 *  «параметры есть, а данных входа нет» означает ровно одно: приложение
 *  открыли по ссылке, а не кнопкой бота. Подписи в такой ситуации нет
 *  и взяться ей неоткуда — предлагать «попробовать снова» бессмысленно,
 *  нужно вернуть человека в бота.
 */
export function isInAppBrowser(): boolean {
  return (
    getWebApp() !== null &&
    getInitData().length === 0 &&
    window.location.hash.includes("tgWebApp")
  );
}

/** Скрипт Telegram загрузился, но данных входа нет. */
export function isTelegramWithoutInitData(): boolean {
  return getWebApp() !== null && getInitData().length === 0;
}

/** Что видно о запуске. Нужно, когда вход не сработал: без этих
 *  строк «не работает» невозможно отличить от «открыли не оттуда». */
/** Имена параметров запуска из адреса. Именно имена, без значений:
 *  в `tgWebAppData` лежит подпись и профиль человека. */
function launchParamNames(): string[] {
  return [
    ...new Set(
      window.location.hash
        .replace(/^#/, "")
        .split("&")
        .map((pair) => pair.split("=")[0])
        .filter(Boolean),
    ),
  ];
}

/** То же из хранилища SDK: он кладёт туда разобранные параметры и
 *  восстанавливает их при перезагрузке страницы. Если там данные есть,
 *  а в адресе нет, значит их потеряли уже после первого запуска. */
function storedParamNames(): string[] {
  try {
    const raw = sessionStorage.getItem("__telegram__initParams");
    return raw ? Object.keys(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}

export function describeLaunch(): string[] {
  const webApp = getWebApp();
  if (!webApp) {
    return ['скрипт Telegram не загрузился'];
  }

  const user = (webApp.initDataUnsafe as { user?: { id?: number } })?.user;
  return [
    `платформа: ${(webApp as unknown as { platform?: string }).platform ?? '—'}`,
    `версия Bot API: ${webApp.version || '—'}`,
    `данные входа: ${webApp.initData.length} симв.`,
    `параметры в адресе: ${window.location.hash.length} симв.`,
    `пользователь: ${user?.id ?? 'не передан'}`,
    // Имена, не значения: в значениях лежит подпись и профиль.
    `ключи запуска: ${launchParamNames().join(', ') || '—'}`,
    `сохранённые: ${storedParamNames().join(', ') || '—'}`,
  ];
}

/** Цвет базальта — им же красим и обвязку Telegram вокруг приложения. */
const BASALT = '#0f1113';
const BASALT_DEEP = '#0b0d0f';

/** Палитра у приложения своя, и цвета клиента в неё больше не едут.
 *
 *  Раньше сюда переносились themeParams, и приложение выглядело
 *  продолжением мессенджера. Теперь оно узнаётся само — а Telegram
 *  просится покрасить свою шапку и нижнюю полосу в наш базальт, иначе
 *  тёмный экран сидит в светлой рамке и выглядит вставленным по ошибке.
 */
export function applyBrand(): void {
  const webApp = getWebApp();
  if (!webApp) return;

  document.documentElement.dataset.theme = 'dark';

  // Методы появились в разных версиях Bot API; в старых клиентах
  // их просто нет, и это не повод падать.
  try {
    webApp.setHeaderColor?.(BASALT);
    webApp.setBackgroundColor?.(BASALT);
    webApp.setBottomBarColor?.(BASALT_DEEP);
  } catch {
    /* клиент не умеет — рамка останется своей */
  }
}

/** Отступы безопасной зоны в CSS-переменные.
 *  В полноэкранном режиме шапка приложения иначе уезжает под «чёлку». */
function applySafeArea(): void {
  const webApp = getWebApp();
  if (!webApp) return;

  const root = document.documentElement;
  const content = webApp.contentSafeAreaInset;
  const area = webApp.safeAreaInset;

  const top = (area?.top ?? 0) + (content?.top ?? 0);
  root.style.setProperty('--safe-top', `${top}px`);
  root.style.setProperty('--safe-bottom', `${area?.bottom ?? 0}px`);
}

export function initTelegram(): void {
  const webApp = getWebApp();
  if (!webApp) return;

  webApp.ready();
  webApp.expand();

  // Полноэкранный режим появился в Bot API 8.0. В старых клиентах
  // метода просто нет — expand() выше уже растянул окно на всю высоту,
  // так что деградация мягкая.
  //
  // Только на телефонах. На десктопе мини-апп и так открыт отдельным
  // окном: разворачивать его на весь экран незачем, а вызов приводит
  // к пересозданию окна.
  const mobile = ["ios", "android"].includes(
    (webApp as unknown as { platform?: string }).platform ?? "",
  );
  try {
    if (mobile) {
      webApp.requestFullscreen?.();
    }
    // Иначе свайп вниз по контенту сворачивает приложение вместо
    // прокрутки — на длинной карточке мероприятия это раздражает.
    webApp.disableVerticalSwipes?.();
  } catch {
    /* клиент не умеет — ничего страшного */
  }

  applyBrand();
  applySafeArea();

  webApp.onEvent('themeChanged', applyBrand);
  webApp.onEvent('safeAreaChanged', applySafeArea);
  webApp.onEvent('contentSafeAreaChanged', applySafeArea);
  webApp.onEvent('fullscreenChanged', applySafeArea);
}
